import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Heart, Users, Globe, BookOpen } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const IMAGES = {
  heroMain: "assets/images/hero-main.jpg",
  heroSecondary: "assets/images/donate-impact.jpg",
  rahvai: "https://images.unsplash.com/photo-1566853166891-3936c0191f60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxydXJhbCUyMGFmcmljYW4lMjB2aWxsYWdlJTIwYWlkJTIwZWR1Y2F0aW9ufGVufDB8fHx8MTc3MzI5NjQ5Nnww&ixlib=rb-4.1.0&q=85",
  seeal: "https://images.unsplash.com/photo-1732696683011-2f80bca1f38b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxydXJhbCUyMGFmcmljYW4lMjB2aWxsYWdlJTIwYWlkJTIwZWR1Y2F0aW9ufGVufDB8fHx8MTc3MzI5NjQ5Nnww&ixlib=rb-4.1.0&q=85",
  behavioral: "https://images.unsplash.com/photo-1758273240631-59d44c8f5b66?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxtZW50YWwlMjBoZWFsdGglMjBjb3Vuc2VsaW5nJTIwc3VwcG9ydCUyMGdyb3VwfGVufDB8fHx8MTc3MzI5NjQ5Nnww&ixlib=rb-4.1.0&q=85"
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const stagger = {
  animate: { transition: { staggerChildren: 0.1 } }
};

export default function HomePage() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section - Asymmetric Layout */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden" data-testid="hero-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Content */}
            <motion.div 
              className="order-2 lg:order-1"
              initial="initial"
              animate="animate"
              variants={stagger}
            >
              <motion.div variants={fadeInUp} className="mb-6">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#F7B500]/10 text-[#2D3E55] rounded-full font-body text-sm font-medium">
                  <Heart size={16} className="text-[#F15A24]" />
                  Faith-Based Nonprofit Organization
                </span>
              </motion.div>
              
              <motion.h1 
                variants={fadeInUp}
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2D3E55] tracking-tight mb-6"
              >
                Faith in Action.<br />
                <span className="text-[#F15A24]">Structure for Impact.</span>
              </motion.h1>
              
              <motion.p 
                variants={fadeInUp}
                className="font-body text-lg text-slate-600 leading-relaxed mb-8 max-w-xl"
              >
                His Presence Chapel Inc. provides governance, leadership oversight, and institutional 
                accountability for initiatives that address humanitarian needs, economic empowerment, 
                and community well-being.
              </motion.p>
              
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
                <Link to="/about">
                  <Button 
                    size="lg" 
                    className="bg-[#2D3E55] hover:bg-[#1a2634] text-white font-body font-medium px-8"
                    data-testid="hero-learn-more-btn"
                  >
                    Learn More
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/partnership">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-[#F15A24] text-[#F15A24] hover:bg-[#F15A24] hover:text-white font-body font-medium px-8"
                    data-testid="hero-partner-btn"
                  >
                    Partner With Us
                  </Button>
                </Link>
              </motion.div>
            </motion.div>
            
            {/* Right: Images Stack */}
            <motion.div 
              className="order-1 lg:order-2 relative"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative">
                <img 
                  src={IMAGES.heroSecondary}
                  alt="Compassionate care"
                  className="w-full h-[350px] md:h-[450px] object-cover rounded-2xl shadow-lg"
                  data-testid="hero-image-secondary"
                />
                <img 
                  src={IMAGES.heroMain}
                  alt="Community volunteers"
                  className="absolute -bottom-8 -left-8 w-3/4 h-[200px] md:h-[280px] object-cover rounded-2xl shadow-2xl border-4 border-white"
                  data-testid="hero-image-main"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#F7B500]/20 rounded-full blur-2xl"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Belief Section */}
      <section className="section-padding bg-gradient-to-b from-white to-[#F8FAFC]" data-testid="mission-section">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2D3E55] mb-4">
              Our Foundation
            </h2>
            <p className="font-body text-lg text-slate-600">
              Guided by faith, driven by compassion
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="h-full border-t-4 border-t-[#F15A24] hover:shadow-xl transition-all duration-300" data-testid="mission-card">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-full bg-[#F15A24]/10 flex items-center justify-center mb-6">
                    <Heart className="text-[#F15A24]" size={28} />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-[#2D3E55] mb-4">Our Mission</h3>
                  <p className="font-body text-slate-600 leading-relaxed">
                    To advance compassionate service, empowerment, and community well-being through 
                    initiatives that address humanitarian needs, expand opportunity, and strengthen 
                    support systems for vulnerable populations.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="h-full border-t-4 border-t-[#F7B500] hover:shadow-xl transition-all duration-300" data-testid="vision-card">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-full bg-[#F7B500]/10 flex items-center justify-center mb-6">
                    <Globe className="text-[#F7B500]" size={28} />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-[#2D3E55] mb-4">Our Vision</h3>
                  <p className="font-body text-slate-600 leading-relaxed">
                    A world where individuals and communities have access to the resources, 
                    opportunities, and support needed to live with dignity and hope.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Belief */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Card className="h-full border-t-4 border-t-[#6EC1E4] hover:shadow-xl transition-all duration-300" data-testid="belief-card">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-full bg-[#6EC1E4]/10 flex items-center justify-center mb-6">
                    <BookOpen className="text-[#6EC1E4]" size={28} />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-[#2D3E55] mb-4">Our Belief</h3>
                  <p className="font-body text-slate-600 leading-relaxed">
                    Faith should be expressed not only through worship and teaching but also through 
                    compassionate service that uplifts individuals and strengthens communities.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Spiritual Foundation */}
      <section className="section-padding bg-[#2D3E55] text-white" data-testid="spiritual-section">
        <div className="container-custom">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="mb-8">
              <span className="inline-block px-4 py-2 bg-white/10 rounded-full text-[#F7B500] font-body text-sm font-medium">
                Our Spiritual Foundation
              </span>
            </div>
            <blockquote className="font-heading text-2xl md:text-3xl lg:text-4xl italic leading-relaxed mb-6">
              "For I was hungry and you gave me something to eat."
            </blockquote>
            <p className="font-body text-lg text-white/80 mb-8">— Matthew 25:35</p>
            <p className="font-body text-white/70 leading-relaxed max-w-2xl mx-auto">
              His Presence Chapel Inc. provides the spiritual inspiration, leadership oversight, 
              and governance structure that guides the humanitarian and community initiatives 
              carried out through its programs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Impact Ecosystem - Bento Grid */}
      <section className="section-padding" data-testid="impact-ecosystem-section">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-2 bg-[#F15A24]/10 rounded-full text-[#F15A24] font-body text-sm font-medium mb-4">
              Impact Ecosystem
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2D3E55] mb-4">
              Our Network of Initiatives
            </h2>
            <p className="font-body text-lg text-slate-600">
              Each initiative focuses on a specific area of service while contributing to a shared 
              mission of strengthening communities and restoring hope.
            </p>
          </motion.div>

          {/* Bento Grid Layout */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* RAHVAI - Large Card */}
            <motion.div
              className="lg:col-span-2 lg:row-span-1"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="h-full overflow-hidden group impact-card" data-testid="rahvai-card">
                <div className="relative h-64 lg:h-72">
                  <img 
                    src={IMAGES.rahvai}
                    alt="RAHVAI - African village aid"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2D3E55]/90 via-[#2D3E55]/40 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <span className="inline-block px-3 py-1 bg-[#2D3E55] rounded-full text-xs font-medium mb-3">
                      Humanitarian Outreach
                    </span>
                    <h3 className="font-heading text-2xl font-semibold mb-2">RAHVAI</h3>
                    <p className="font-body text-sm text-white/80 mb-2">
                      Rehabilitation and Hope for the Vulnerable in Africa Initiative
                    </p>
                    <p className="font-body text-white/70 text-sm hidden md:block">
                      Community relief efforts, water access initiatives, and empowerment programs 
                      designed to improve quality of life.
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* SEEAL Africa */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="h-full overflow-hidden group impact-card" data-testid="seeal-card">
                <div className="relative h-64 lg:h-full min-h-[280px]">
                  <img 
                    src={IMAGES.seeal}
                    alt="SEEAL Africa - Economic empowerment"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#F15A24]/90 via-[#F15A24]/40 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <span className="inline-block px-3 py-1 bg-[#F15A24] rounded-full text-xs font-medium mb-3">
                      Economic Empowerment
                    </span>
                    <h3 className="font-heading text-xl font-semibold mb-2">SEEAL Africa</h3>
                    <p className="font-body text-sm text-white/80">
                      Skills. Enterprise. Access. Livelihood.
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Behavioral Health */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Card className="overflow-hidden group impact-card" data-testid="behavioral-card">
                <div className="grid md:grid-cols-2">
                  <div className="relative h-64 md:h-80">
                    <img 
                      src={IMAGES.behavioral}
                      alt="Behavioral Health Navigation"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8 flex flex-col justify-center bg-gradient-to-br from-[#6EC1E4]/10 to-white">
                    <span className="inline-block w-fit px-3 py-1 bg-[#6EC1E4] text-white rounded-full text-xs font-medium mb-4">
                      Behavioral Health Support
                    </span>
                    <h3 className="font-heading text-2xl font-semibold text-[#2D3E55] mb-4">
                      Behavioral Health Navigation & Workforce Agency
                    </h3>
                    <p className="font-body text-slate-600 mb-4">
                      Supporting individuals and families navigating behavioral health systems while 
                      strengthening the workforce that serves these communities.
                    </p>
                    <ul className="font-body text-sm text-slate-600 space-y-2">
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-[#6EC1E4] rounded-full"></span>
                        Navigation assistance
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-[#6EC1E4] rounded-full"></span>
                        Community guidance
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-[#6EC1E4] rounded-full"></span>
                        Veteran support component
                      </li>
                    </ul>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>

          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Link to="/impact">
              <Button 
                size="lg"
                className="bg-[#F15A24] hover:bg-[#d64d1e] text-white font-body font-medium px-8"
                data-testid="view-impact-btn"
              >
                Explore Our Initiatives
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-[#F15A24] to-[#D64545]" data-testid="cta-section">
        <div className="container-custom">
          <motion.div 
            className="text-center text-white max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-6">
              Join Us in Making a Difference
            </h2>
            <p className="font-body text-lg text-white/90 mb-8">
              Meaningful impact is achieved through collaboration. Partner with us to strengthen 
              program delivery and expand opportunities for the communities we serve.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/partnership">
                <Button 
                  size="lg"
                  className="bg-white text-[#F15A24] hover:bg-white/90 font-body font-medium px-8"
                  data-testid="cta-become-partner-btn"
                >
                  Become a Partner
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 font-body font-medium px-8"
                  data-testid="cta-contact-btn"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
